import CKEditor, {
    type ckeditorEditor,
    type ckeditorEvent,
    type ICKEditorProps,
} from "ckeditor4-react";
import { memo, useRef } from "react";

import { cn } from "@btnet/utilities-core";

import { ShowCharactersRemainingThreshold } from "types/constants";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isUnitTest } from "utilities/environment/environment";
import { getReadOnly } from "utilities/filters/filter";
import { embeddedImagesHtmlLength } from "utilities/form/yup";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { CKEditorDisplay } from "commonComponents/utilities/CKEditorDisplay/CKEditorDisplay";
import { useDebouncedOnChange } from "commonComponents/utilities/Input/InputDebouncingContext";
import ReadMore from "commonComponents/utilities/ReadMore/ReadMore";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import "./BTCKEditor.less";

/**
 * Returns CKEditor's current content when (and only when) the `change`
 * event represents an actual user edit — otherwise `undefined`, signalling
 * the caller to skip propagating to form state.
 *
 * Why this gate exists (Bug 275021):
 * CKEditor 4 fires `change` after every `setData()` — including the one
 * `ckeditor4-react` triggers from componentDidUpdate when our controlled
 * `value` prop changes externally (e.g., Formik's `enableReinitialize`
 * after a save round-trip refreshes the field from the server). On that
 * synthetic event, `editor.getData()` returns CKEditor's *canonicalized*
 * HTML, which is NOT byte-identical to the data we set: table cells get
 * `<p>` wrappers, nested spans get merged/re-emitted, etc. For large
 * rich-text values the canonical form can be several times bigger than
 * what was stored (observed: a 121,867-char T&C ballooned to 512,241),
 * which then trips the maxLength validator on a value the server only
 * ever saw as well under the cap — and locks the user out of saving.
 *
 * Why focus and NOT checkDirty():
 * `editor.setData()` does NOT reset CKEditor's dirty baseline — only init,
 * mode-change, and `contentDom` call `resetDirty()`. So `checkDirty()`
 * returns `true` after ANY programmatic `setData()` (the freshly loaded
 * snapshot differs from the stale baseline), which makes it useless for
 * telling a user edit apart from a data-load echo.
 *
 * Focus is the reliable discriminator: in classic mode `setData()`
 * canonicalizes and writes the DOM but never calls `focus()`, so a
 * programmatic data load fires `change` while the editor is unfocused.
 * Genuine user edits (typing, paste, source-mode edits, toolbar actions)
 * happen while the editor has focus. The 200ms blur delay is long gone by
 * the time a save → server round-trip → reload → reinitialize completes.
 *
 * `getData()` is deliberately not called on the unfocused path — it is
 * the operation that produces the inflated canonical blob, and we have
 * no use for the result there.
 */
export function valueIfUserEdit(editor: ckeditorEditor): string | undefined {
    return editor.focusManager?.hasFocus ? editor.getData() : undefined;
}

// todo migrate after webforms
(CKEditor as any).editorUrl = `${window.location.origin}/jQuery/ckeditor/ckeditor.js`;

export class CKEditorResponse {
    constructor(data: any) {
        this.value = data.value;
        this.readOnly = getReadOnly(data.validators);
    }

    value: string;
    readOnly: boolean;
}

interface IBTCKEditorProps<FormValues>
    extends Omit<ICKEditorProps, "data" | "onChange" | "onBlur"> {
    id: KeyOfOrString<FormValues>;

    /** html data to display within the CKEditor */
    value: string | undefined;

    "data-testid": string;
    readOnly?: boolean;

    /**
     * Renders the editor content as a greyed-out, non-editable display instead of a live editor.
     * Like `readOnly` it shows the value statically, but styled as a disabled control.
     */
    disabled?: boolean;

    /** The max number of characters to allow. Should be the same as your max validator */
    maxLength?: number;

    /** pass formik setFieldValue */
    onChange: (field: KeyOfOrString<FormValues>, value: string) => void;

    /** pass formik setFieldTouched */
    onBlur: (id: KeyOfOrString<FormValues>, touched: boolean) => void;

    /** class name for the div containing the CKEditor */
    containerClassName?: string;

    /** If true, will wrap the wrap ckeditor in the read more component **/
    useReadMoreForReadonly?: boolean;
}

const BTCKEditorInternal = track((props) => ({
    element: "CK Editor",
    uniqueId: props["data-testid"],
}))(function <FormValues = undefined>({
    id,
    value,
    onChange,
    onBlur,
    "data-testid": testid,
    readOnly,
    disabled,
    maxLength,
    containerClassName,
    useReadMoreForReadonly,
    ...otherProps
}: IBTCKEditorProps<FormValues> & ITrackingProp) {
    const [debouncedValue, debouncedChange] = useDebouncedOnChange<typeof value, FormValues>(
        value ?? "",
        (field: KeyOfOrString<FormValues>, value: string) => {
            // fire blur event on change so min/max length error appears right away
            onBlur(field, true);
            onChange(field, value);
        },
    );

    const editorRef = useRef<CKEditor>(null);

    function onChangeWrapper(event: ckeditorEvent) {
        const newValue = valueIfUserEdit(event.editor);
        if (newValue !== undefined) {
            debouncedChange(id, newValue);
        }
    }

    function onBlurWrapper() {
        onBlur(id, true);
        otherProps.tracking?.trackEvent({ event: "InputBlur" });
    }

    function onFocusWrapper() {
        otherProps.tracking?.trackEvent({ event: "InputFocus" });
    }

    function onModeWrapper() {
        const editor = editorRef.current?.editor;
        if (editor) {
            if (editor.mode === "source") {
                editor.editable().attachListener(editor.editable(), "input", handleInput);
            } else {
                editor.editable().removeListener("input", handleInput);
            }
        }
    }

    function handleInput() {
        // Bound to the DOM `input` event on the source-mode textarea, which
        // only fires on genuine user typing — never on programmatic setData.
        // So no user-edit gate is needed here (unlike onChangeWrapper, which
        // sees CKEditor's setData echo).
        debouncedChange(id, editorRef.current?.editor?.getData());
    }

    function onDestroyWrapper() {
        editorRef.current?.editor?.removeAllListeners();
    }

    let charactersRemaining: JSX.Element | undefined;

    const valueLength = debouncedValue
        ? debouncedValue.length - embeddedImagesHtmlLength(debouncedValue)
        : 0;
    if (
        maxLength !== undefined &&
        valueLength <= maxLength &&
        valueLength / maxLength >= ShowCharactersRemainingThreshold
    ) {
        // Note: characters remaining past the max length limit should be handled by a max length validator on your form values
        const charCount = maxLength - valueLength;
        charactersRemaining = (
            <span>
                {charCount} {charCount === 1 ? "character" : "characters"} remaining
            </span>
        );
    }

    // todo remove this check when ckeditor4react fixes the destory() bug
    const isRunningInTestEnv = isUnitTest();

    const showAsStatic = readOnly || disabled;

    const className = cn("BTCKEditor", containerClassName, {
        "BTCKEditor--disabled": disabled,
    });

    return (
        <div data-testid={testid} className={className}>
            {showAsStatic && !isRunningInTestEnv && debouncedValue && useReadMoreForReadonly && (
                <ReadMore numberOfLines={9}>
                    <CKEditorDisplay dangerouslySetInnerHTML={{ __html: debouncedValue }} />
                </ReadMore>
            )}
            {showAsStatic && !isRunningInTestEnv && debouncedValue && !useReadMoreForReadonly && (
                <CKEditorDisplay dangerouslySetInnerHTML={{ __html: debouncedValue }} />
            )}
            {showAsStatic && (isRunningInTestEnv || !debouncedValue) && (
                <ValueDisplay value={undefined} />
            )}
            {!showAsStatic && !isRunningInTestEnv && (
                <>
                    {/* eslint-disable-next-line react/forbid-elements */}
                    <CKEditor
                        ref={editorRef}
                        data={debouncedValue}
                        onChange={onChangeWrapper}
                        onBlur={onBlurWrapper}
                        onFocus={onFocusWrapper}
                        onMode={onModeWrapper}
                        onDestroy={onDestroyWrapper}
                        /**
                         * Added as of 07/19/2024
                         * `${window.location.origin}/jQuery/ckeditor/ckeditor.js` is our local source files for ckeditor, as of that version, CKEditor runs
                         * the following:
                         * ```js
                         *  CKEDITOR.domReady(function () {
                         *       !CKEDITOR.disableAutoInline && CKEDITOR.inlineAll();
                         *   });
                         * ```
                         *
                         * The above statement will call inlineAll on load, which in the current version will create an instance of CKEDITOR for all
                         * elements with contentEditable=true. This is dumb behavior and interferes with other editors (like the new BTEditor)
                         * we don't want to interfere with. Therefore, we're using this prop to disable that behavior.
                         *
                         * Also it had to be configured here and not in config.js due to loading order :(
                         */

                        onBeforeLoad={(editorGlobal) => {
                            (editorGlobal as { disableAutoInline: boolean }).disableAutoInline =
                                true;
                        }}
                        {...otherProps}
                    />
                    {charactersRemaining}
                </>
            )}
        </div>
    );
});

export const BTCKEditor = memo(BTCKEditorInternal) as typeof BTCKEditorInternal;
